import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, ArrowLeft } from 'lucide-react';
import api from '../../../../services/api';
import LoadingSpinner from '../../../common/LoadingSpinner';

const DateWiseAttendance = ({ basePath = '/admin/attendance' }) => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [attendanceData, setAttendanceData] = useState({});
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  useEffect(() => {
    fetchMonthAttendance();
    fetchHolidays();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when visible month changes
  }, [currentDate]);

  const fetchMonthAttendance = async () => {
    try {
      setLoading(true);
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      const response = await api.get(`/admin/attendance?month=${month}&year=${year}`);
      
      // Group attendance by date
      const grouped = {};
      response.data.attendances.forEach(att => {
        const attDate = new Date(att.date);
        const dateKey = `${attDate.getFullYear()}-${String(attDate.getMonth() + 1).padStart(2, '0')}-${String(attDate.getDate()).padStart(2, '0')}`;
        if (!grouped[dateKey]) {
          grouped[dateKey] = {
            total: 0,
            fullDay: 0,
            halfDay: 0,
            absent: 0
          };
        }
        grouped[dateKey].total++;
        if (att.status === 'full-day') grouped[dateKey].fullDay++;
        if (att.status === 'half-day') grouped[dateKey].halfDay++;
        if (att.status === 'absent') grouped[dateKey].absent++;
      });
      
      setAttendanceData(grouped);
    } catch (error) {
      console.error('Error fetching attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHolidays = async () => {
    try {
      const response = await api.get('/admin/holidays');
      setHolidays(response.data || []);
    } catch (error) {
      console.error('Error fetching holidays:', error);
    }
  };

  const isHoliday = (date) => {
    if (!date) return null;
    return holidays.find(h => {
      const holidayDate = new Date(h.date);
      // Compare year, month, and day separately to avoid timezone issues
      return holidayDate.getFullYear() === date.getFullYear() &&
             holidayDate.getMonth() === date.getMonth() &&
             holidayDate.getDate() === date.getDate();
    });
  };

  const isSunday = (date) => {
    if (!date) return false;
    return date.getDay() === 0;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const handlePreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleDateClick = (date) => {
    if (date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      navigate(`${basePath}/date-wise/${dateString}`);
    }
  };

  const getDateStats = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateKey = `${year}-${month}-${day}`;
    return attendanceData[dateKey];
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isFutureDate = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date > today;
  };

  const days = getDaysInMonth(currentDate);

  if (loading) {
    return <LoadingSpinner size="lg" className="mt-8" />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(basePath)}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="font-medium">Back</span>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Date-wise Attendance</h1>
            <p className="text-gray-600">Click on any date to view detailed attendance</p>
          </div>
        </div>
      </div>

      {/* Calendar Card */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Month Navigation */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={handlePreviousMonth}
              className="p-2 hover:bg-blue-400 rounded-lg transition-colors"
            >
              <ChevronLeft className="h-6 w-6 text-white" />
            </button>
            
            <div className="flex items-center space-x-3">
              <CalendarIcon className="h-6 w-6 text-white" />
              <h2 className="text-2xl font-bold text-white">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
            </div>
            
            <button
              onClick={handleNextMonth}
              className="p-2 hover:bg-blue-400 rounded-lg transition-colors"
            >
              <ChevronRight className="h-6 w-6 text-white" />
            </button>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="p-6 overflow-x-auto">
        <div className="min-w-[900px]">
          {/* Day Headers */}
            <div className="grid grid-cols-7 gap-2 mb-2">
            {dayNames.map((day) => (
              <div
                key={day}
                className="text-center font-semibold text-gray-600 text-sm py-2"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-2">
            {days.map((date, index) => {
              const stats = getDateStats(date);
              const hasAttendance = stats && stats.total > 0;
              const today = isToday(date);
              const future = isFutureDate(date);
              const holidayInfo = isHoliday(date);
              const sunday = isSunday(date);

              return (
                <div
                  key={index}
                  onClick={() => !future && handleDateClick(date)}
                  className={`
                    min-h-24 p-3 rounded-xl border-2 transition-all duration-200
                    ${!date ? 'bg-gray-50 border-transparent cursor-default' : ''}
                    ${date && !future ? 'cursor-pointer hover:shadow-lg hover:scale-105' : ''}
                    ${future ? 'bg-gray-50 border-gray-200 cursor-not-allowed opacity-50' : ''}
                    ${today && !future ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}
                    ${holidayInfo && !today && !future ? 'bg-sky-100 border-sky-300 hover:bg-sky-200' : ''}
                    ${sunday && !holidayInfo && !today && !future ? 'bg-green-100 border-green-400 hover:bg-green-200' : ''}
                    ${hasAttendance && !today && !future && !holidayInfo && !sunday ? 'bg-gradient-to-br from-green-50 to-blue-50 hover:from-green-100 hover:to-blue-100' : ''}
                    ${!hasAttendance && date && !today && !future && !holidayInfo && !sunday ? 'bg-white hover:bg-gray-50' : ''}
                  `}
                >
                  {date && (
                    <div className="h-full flex flex-col">
                      <div className="flex items-center justify-between mb-2">
                        <span className={`
                          text-lg font-bold
                          ${today ? 'text-blue-600' : holidayInfo ? 'text-sky-700' : sunday ? 'text-green-700' : 'text-gray-700'}
                        `}>
                          {date.getDate()}
                        </span>
                        {today && (
                          <span className="text-xs font-semibold text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                            Today
                          </span>
                        )}
                      </div>

                      {/* Holiday Badge
                      {holidayInfo && !future && (
                        <div className="mb-2 bg-sky-200 border border-sky-400 rounded px-2 py-1">
                          <div className="text-xs font-semibold text-sky-900 text-center">
                            {holidayInfo.description}
                          </div>
                        </div>
                      )} */}

                      {/* Holiday Badge — holidays are paid full days, same as Sunday */}
                        {holidayInfo && !future && (
                        <div className="mb-2 bg-sky-200 border border-sky-400 rounded px-2 py-1">
                          <div className="text-xs font-semibold text-sky-900 text-center">
                            {holidayInfo.description}
                          </div>
                          <div className="text-xs font-semibold text-sky-900 text-center">
                            Full Day
                          </div>
                        </div>
                      )}

                      {/* Sunday = Full Day */}
                      {sunday && !holidayInfo && !future && (
                        <div className="mb-2 bg-green-200 border border-green-400 rounded px-2 py-1">
                          <div className="text-xs font-semibold text-green-900 text-center">
                            Full Day
                          </div>
                        </div>
                      )}

                      {hasAttendance && !future && (
                        <div className="space-y-1 text-xs">
                          <div className="flex items-center justify-between bg-white rounded px-2 py-1">
                            <span className="text-gray-600">Total</span>
                            <span className="font-bold text-gray-900">{stats.total}</span>
                          </div>
                          {stats.fullDay > 0 && (
                            <div className="flex items-center justify-between bg-green-100 rounded px-2 py-1">
                              <span className="text-green-700">Full</span>
                              <span className="font-bold text-green-800">{stats.fullDay}</span>
                            </div>
                          )}
                          {stats.halfDay > 0 && (
                            <div className="flex items-center justify-between bg-yellow-100 rounded px-2 py-1">
                              <span className="text-yellow-700">Half</span>
                              <span className="font-bold text-yellow-800">{stats.halfDay}</span>
                            </div>
                          )}
                          {stats.absent > 0 && (
                            <div className="flex items-center justify-between bg-red-100 rounded px-2 py-1">
                              <span className="text-red-700">Absent</span>
                              <span className="font-bold text-red-800">{stats.absent}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {!hasAttendance && !future && (
                        <div className="flex-1 flex items-center justify-center">
                          {!holidayInfo && !sunday && (
                            <span className="text-xs text-gray-400">No records</span>
                          )}
                        </div>
                      )}

                      {future && (
                        <div className="flex-1 flex items-center justify-center">
                          <span className="text-xs text-gray-400">Future</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </div>
        </div>

        {/* Legend */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <span className="font-semibold text-gray-700">Legend:</span>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded bg-green-100 border-2 border-green-200"></div>
              <span className="text-gray-600">Full Day</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded bg-yellow-100 border-2 border-yellow-200"></div>
              <span className="text-gray-600">Half Day</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded bg-red-100 border-2 border-red-200"></div>
              <span className="text-gray-600">Absent</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded bg-sky-100 border-2 border-sky-300"></div>
              <span className="text-gray-600">Holiday</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded bg-blue-50 border-2 border-blue-500"></div>
              <span className="text-gray-600">Today</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-gray-600"></span>
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Total Full Days</p>
              <p className="text-3xl font-bold mt-1">
                {Object.values(attendanceData).reduce((sum, day) => sum + day.fullDay, 0)}
              </p>
            </div>
            <div className="bg-green-400 bg-opacity-30 rounded-full p-3">
              <CalendarIcon className="h-8 w-8" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-100 text-sm font-medium">Total Half Days</p>
              <p className="text-3xl font-bold mt-1">
                {Object.values(attendanceData).reduce((sum, day) => sum + day.halfDay, 0)}
              </p>
            </div>
            <div className="bg-yellow-400 bg-opacity-30 rounded-full p-3">
              <CalendarIcon className="h-8 w-8" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-sm font-medium">Total Absents</p>
              <p className="text-3xl font-bold mt-1">
                {Object.values(attendanceData).reduce((sum, day) => sum + day.absent, 0)}
              </p>
            </div>
            <div className="bg-red-400 bg-opacity-30 rounded-full p-3">
              <CalendarIcon className="h-8 w-8" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DateWiseAttendance;
